import { z } from "zod";
import { BearingDeg, SpeedKnots } from "./geometry.js";
import { TimedEvent } from "./world.js";

/**
 * Commands flow from a client to a RadarStateSource. Implementations route
 * appropriately: LocalSimSource mutates state directly, DurableObjectSource
 * sends over WebSocket, ReplaySource rejects all commands.
 *
 * Per-client display state (range scale, gain, EBL/VRM positions, chrome
 * theme) is NOT in this channel. Display state stays local and never round
 * trips through the source.
 */
export const Command = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("pause") }),
  z.object({ kind: z.literal("resume") }),
  z.object({ kind: z.literal("set_time_scale"), scale: z.number().positive() }),
  z.object({
    kind: z.literal("set_own_cog"),
    cog: BearingDeg,
  }),
  z.object({
    kind: z.literal("set_own_sog"),
    sog: SpeedKnots,
  }),
  z.object({
    kind: z.literal("inject_event"),
    timed: TimedEvent,
  }),
  /**
   * Acquire a target for MARPA tracking. The acquisition itself is a
   * client-side concern (each client tracks its own targets), but the
   * command is included for cases where the source needs to know
   * (e.g. an instructor-shared cursor).
   */
  z.object({
    kind: z.literal("acquire_target"),
    vesselId: z.string(),
  }),
  z.object({
    kind: z.literal("drop_target"),
    vesselId: z.string(),
  }),
]);
export type Command = z.infer<typeof Command>;

export const CommandResult = z.discriminatedUnion("ok", [
  z.object({ ok: z.literal(true) }),
  z.object({
    ok: z.literal(false),
    reason: z.string(),
    code: z.enum([
      "rejected_by_source",
      "unauthorised",
      "invalid_state",
      "unknown_target",
    ]),
  }),
]);
export type CommandResult = z.infer<typeof CommandResult>;
